import { http, queryString } from 'utils'
import { LocationDecorator } from 'decorators'
import { findIndex, omit, concat, map, trim } from 'lodash'

// Location mutation imports.
import {
  SET_LOCATIONS, SET_LOCATIONS_LIST, SET_LOCATIONS_PAGINATION, ADD_LOCATIONS, SET_LOCATION,
  SET_LOCATION_ATTRIBUTE, RESET_LOCATION, ADD_LOCATION
} from 'vx/mutations'

const initialLocationState = () => {
  return {
    id: null,
    user_id: null,
    name: null,
    street_number: null,
    street_name: null,
    suburb: null,
    state: null,
    postcode: null,
    uhf_channel: null,
    hours_of_loading: null,
    file: null,
    latitude: null,
    longitude: null,
    directions: null,
    contact_name: null,
    contact_phone: null,
    contact_second_phone: null,
    contact_email: null,
    photo_url: null,
    photo_name: null,
    secondary_contact_name: null
  }
}

// Location state.
const state = {
  locations: [],
  locationsList: [],
  location: initialLocationState()
}

// Location state getters.
const getters = {
  /**
   * Locations decorated via the LocationDecorator.
   */
  locationsDecorated: state => map(state.locations, location => (new LocationDecorator(location))),

  /**
   * Location options for selectize.
   */
  locationOptions: state => map(state.locationsList, location => ({ id: location.id, text: trim(`${location.name} ${location.suburb}`) })),

  /**
   * Decorated location.
   */
  locationDecorated: state => (new LocationDecorator(state.location))
}

// Location actions.
const actions = {
  /**
  * Get a list of locations.
  */
  getLocations ({ commit }, payload) {
    return http.get(`locations?${queryString(payload)}`).then(response => {
      commit(SET_LOCATIONS, response.data.data)
      commit(SET_LOCATIONS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Get Location List for Select
   */
  getLocationsList ({ commit }, payload) {
    return http.get(`locations/list?${queryString(payload)}`).then(response => {
      commit(SET_LOCATIONS_LIST, response.data.data)

      return response
    })
  },

  /**
   * Get the locations for the next page.
   */
  getLocationsForNextPage ({ commit, state }, payload) {
    if (state.pagination.current_page === state.pagination.last_page) {
      return Promise.reject('There are no more locations to load.')
    }

    let page = state.pagination.current_page + 1

    return http.get(`locations?${queryString(payload)}&page=${page}`).then(response => {
      commit(ADD_LOCATIONS, response.data.data)
      commit(SET_LOCATIONS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Store a new location.
   */
  storeLocation ({ commit }, payload) {
    return http.post(`locations`, payload)
  },

  /**
   * Update an existing location's data.
   */
  updateLocation ({ commit }, {id, payload}) {
    return http.put(`locations/${id}`, payload).then(response => {
      commit(SET_LOCATION, response.data)

      return response
    })
  },

  /**
   * Retrieve a location by its ID.
   */
  getLocation ({ commit }, id) {
    return http.get(`locations/${id}`).then(response => {
      commit(SET_LOCATION, response.data)

      return response
    })
  },

  /**
   * Delete a location.
   */
  deleteLocation ({ commit }, id) {
    return http.delete(`locations/${id}`).then(response => {
      commit(SET_LOCATION, initialLocationState())

      return response
    })
  },

  /**
   * Upload a photo file to the server.
   */
  uploadLocationPhoto (store, { file, id }) {
    let payload = new FormData()

    payload.append('photo', file)

    return http.post(`locations/${id}/access-photo`, payload)
  },

  /**
   * Delete a location photo.
   */
  deleteLocationPhoto (store, id) {
    return http.delete(`locations/${id}/access-photo`)
  },

  /**
   * Set a location as a favourite of the logged in user.
   */
  favouriteLocation ({ commit }, id) {
    return http.post(`locations/favourite/${id}`).then(response => {
      commit(ADD_LOCATION, response.data)

      return response
    })
  }
}

// Location state mutations.
const mutations = {
  /**
   * Set the locations.
   */
  [SET_LOCATIONS] (state, locations) {
    state.locations = locations
  },

  /**
   * Set the locations.
   */
  [SET_LOCATIONS_LIST] (state, locations) {
    state.locationsList = locations
  },

  /**
   * Add more locations to the locations state.
   */
  [ADD_LOCATIONS] (state, locations) {
    state.locations = concat(state.locations, locations)
  },

  /**
   * Set the location pagination.
   */
  [SET_LOCATIONS_PAGINATION] (state, pagination) {
    state.pagination = pagination
  },

  /**
   * Set a location attribute.
   */
  [SET_LOCATION_ATTRIBUTE] (state, { attribute, value }) {
    if (attribute === 'user_id' && value === null) {
      state.location[attribute] = ''
    } else {
      state.location[attribute] = value
    }
  },

  /**
   * Set the location.
   */
  [SET_LOCATION] (state, location) {
    state.location = location
  },

  /**
   * Reset the location.
   */
  [RESET_LOCATION] (state) {
    state.location = initialLocationState()
  },

  /**
   * Add or replace a location.
   */
  [ADD_LOCATION] (state, location) {
    let existingLocation = findIndex(state.locations, existing => existing.id === location.id)

    if (existingLocation === -1) {
      state.locations.push(location)
    } else {
      state.locations.splice(existingLocation, 1, location)
    }
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations
}



// WEBPACK FOOTER //
// ./src/vuex/modules/location.js